import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ExternalLink, RotateCcw } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError, wibDaysAgo } from "../lib/api";
import { PageHeader, Panel } from "../lib/ui";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const DATA_STATUS = {
  available: { label: "Data Tersedia", className: "bg-status-passed/15 text-status-passed" },
  no_import: { label: "Belum Ada Import", className: "bg-taupe/20 text-cocoa" },
  cashier_only_no_file: { label: "Ada Kasir, File Belum Masuk", className: "bg-amber-100 text-amber-800" },
};

export default function OnlineReportCoverage() {
  const navigate = useNavigate();
  const [date, setDate] = useState(wibDaysAgo(1));
  const [data, setData] = useState({ coverage: [], unconfigured: [] });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/marketplace-shipping/coverage?date=${date}`);
      setData(res.data);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  return (
    <div data-testid="online-coverage-page">
      <PageHeader
        title="Cakupan Laporan Online"
        subtitle="Status data crosscheck pengiriman per toko marketplace pada tanggal laporan"
      >
        <Button data-testid="coverage-refresh" disabled={loading} onClick={load} variant="outline">
          <RotateCcw className="mr-1.5 h-4 w-4" />
          Muat Ulang
        </Button>
      </PageHeader>

      <Panel className="mb-5 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label htmlFor="coverage-date">Tanggal Laporan (WIB)</Label>
            <Input
              data-testid="coverage-date-input"
              id="coverage-date"
              onChange={(event) => setDate(event.target.value)}
              type="date"
              value={date}
            />
          </div>
          <div className="text-sm text-wmuted">
            Dasar tanggal: tanggal pengiriman file. Satu baris per toko terkonfigurasi.
          </div>
        </div>
      </Panel>

      <Panel className="p-4">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Platform</TableHead>
                <TableHead>Toko (Customer)</TableHead>
                <TableHead>Status Data</TableHead>
                <TableHead>Import Terakhir</TableHead>
                <TableHead className="text-center">Order MP</TableHead>
                <TableHead className="text-center">Kasir</TableHead>
                <TableHead className="text-center">Cocok</TableHead>
                <TableHead className="text-center">Beda Brg/Qty</TableHead>
                <TableHead className="text-center">Blm Verifikasi</TableHead>
                <TableHead className="text-center">MP tanpa Kasir</TableHead>
                <TableHead className="text-center">Kasir tanpa File</TableHead>
                <TableHead className="text-center">Outstanding</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.coverage.length === 0 && (
                <TableRow>
                  <TableCell className="text-center text-wmuted" colSpan={13}>
                    Belum ada toko marketplace yang dikonfigurasi. Buat mapping toko dulu.
                  </TableCell>
                </TableRow>
              )}
              {data.coverage.map((row) => {
                const meta = DATA_STATUS[row.data_status] || DATA_STATUS.no_import;
                return (
                  <TableRow key={row.store_mapping_id} data-testid={`coverage-row-${row.store_mapping_id}`}>
                    <TableCell className="uppercase">{row.platform}</TableCell>
                    <TableCell className="font-medium text-cocoa">{row.customer_name}</TableCell>
                    <TableCell>
                      <Badge className={`border-0 ${meta.className}`}>{meta.label}</Badge>
                      {row.has_unconfirmed && (
                        <span className="ml-1 inline-flex items-center gap-1 text-xs text-status-review">
                          <AlertTriangle className="h-3 w-3" />identitas belum pasti
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-wmuted">
                      {row.last_import ? row.last_import.slice(0, 16).replace("T", " ") : "-"}
                      {row.file_coverage && <div>{row.file_coverage}</div>}
                    </TableCell>
                    <TableCell className="text-center">{row.marketplace_orders}</TableCell>
                    <TableCell className="text-center">{row.cashier_txn}</TableCell>
                    <TableCell className="text-center text-status-passed">{row.full_match}</TableCell>
                    <TableCell className="text-center text-status-reject">{row.variant_qty_diff}</TableCell>
                    <TableCell className="text-center text-status-review">{row.needs_mapping}</TableCell>
                    <TableCell className="text-center text-status-review">{row.marketplace_only}</TableCell>
                    <TableCell className="text-center text-status-reject">{row.cashier_only}</TableCell>
                    <TableCell className="text-center font-semibold">{row.outstanding}</TableCell>
                    <TableCell>
                      {row.batch_id && (
                        <Button
                          data-testid={`coverage-detail-${row.store_mapping_id}`}
                          onClick={() => navigate("/finance/shipping-reconciliation")}
                          size="sm"
                          variant="outline"
                        >
                          <ExternalLink className="mr-1 h-3 w-3" />Detail
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Panel>

      {data.unconfigured.length > 0 && (
        <Panel className="mt-5 p-4" data-testid="coverage-unconfigured">
          <div className="mb-2 font-semibold text-cocoa">Belum Dikonfigurasi</div>
          <div className="text-sm text-wmuted">
            Customer jalur marketplace berikut belum dipetakan ke toko. Data mereka tidak ditebak — petakan di menu Mapping Toko & Varian.
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {data.unconfigured.map((customer) => (
              <Badge className="border-taupe/40 bg-transparent text-cocoa" key={customer.id} variant="outline">
                {customer.name} · {customer.marketplace || "-"}
              </Badge>
            ))}
          </div>
        </Panel>
      )}
    </div>
  );
}
